<template>
  <div class="game-wrapper" ref="wrapper" :style="{ background: backgroundColor }">
    <canvas
      ref="gameCanvas"
      @mousedown="handleInput"
      @touchstart.prevent="handleInput"
    ></canvas>

    <div v-if="state === 'playing' || state === 'paused'" class="hud-layer">
      <div class="box-scores">
        <div class="level-box">
          <div class="label">{{ levelLabel }}</div>
          <div class="value">{{ Math.floor(level + 1) }}</div>
        </div>
        <div class="score-box">
          <div class="label">{{ scoreLabel }}</div>
          <div class="value">{{ Math.floor(score) }}</div>
        </div>
        <div v-if="combo > 1" class="combo-badge">
          <div class="label">{{ comboLabel }}</div>
          <div class="value">x{{ combo }}</div>
        </div>
      </div>

      <div class="hud-controls">
        <button class="control-btn1" @click.stop="togglePause">
          <v-icon color="white">{{
            state === "paused" ? "mdi-play" : "mdi-pause"
          }}</v-icon>
        </button>
        <button class="control-btn2" @click.stop="toggleSound">
          <v-icon color="white">{{
            soundEnabled ? "mdi-volume-high" : "mdi-volume-off"
          }}</v-icon>
        </button>
        <button class="control-btn3" @click.stop="showHelp = true">
          <v-icon color="white">mdi-help</v-icon>
        </button>
      </div>
    </div>

    <div v-if="state === 'start'" class="overlay">
      <div class="menu-card">
        <h1 class="game-title">{{ title }}</h1>
        <img
          v-if="value.images.hero"
          :src="value.images.hero"
          class="hero-img floating pulse"
        />
        <img
          v-else
          :src="require('@/assets/images/game/meeq_bg.webp')"
          class="hero-img floating pulse"
        />
        <p>{{ subtitle }}</p>
        <button @click="startGame" class="start-btn">{{ startBtn }}</button>
        <button @click="showHelp = true" class="help-link">{{ helpBtn }}</button>
      </div>
    </div>

    <div v-if="state === 'getPhone'" class="overlay">
      <div class="menu-card phone-card">
        <h2 class="mb-2">{{ submitScore }}</h2>
        <p>{{ phonePrompt }}</p>

        <div class="input-group">
          <input
            v-model="phoneNumber"
            type="tel"
            :placeholder="phonePlaceholder"
            maxlength="11"
            class="custom-input"
          />
        </div>

        <div class="menu-btns mt-4">
          <button
            class="start-btn sm"
            :disabled="!isValidPhone || submitting"
            @click="submitPhoneAndRestart"
          >
            {{ submitting ? submittingText : submitAndRestart }}
          </button>
          <button class="help-link" @click="state = 'gameover'">{{ cancel }}</button>
        </div>
      </div>
    </div>

    <div v-if="state === 'gameover'" class="overlay">
      <div class="menu-card">
        <h1 class="boom-text">{{ gameOver }}</h1>
        <img
          v-if="value.images.hero"
          :src="value.images.hero"
          class="hero-img floating pulse"
        />
        <img
          v-else
          :src="require('@/assets/images/game/meeq_bg.webp')"
          class="hero-img floating pulse"
        />
        <div class="stats-grid">
          <div class="stat-item">
            <span>{{ finalScore }}:</span>
            <strong>{{ Math.floor(score) }}</strong>
          </div>
          <div class="stat-item">
            <span>{{ maxComboLabel }}:</span>
            <strong>{{ maxCombo }}</strong>
          </div>
        </div>
        <p v-if="isNewRecord" class="new-record">{{ newRecordMessage }}</p>

        <div class="menu-btns">
          <button @click="startGame" class="start-btn">{{ restartBtn }}</button>
          <button
            v-if="requirePhoneNumber && Math.floor(score) > minScoreToSubmit"
            @click="state = 'getPhone'"
            class="help-link"
          >
            {{ rewardBtn }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showHelp" class="overlay z-top" @click="showHelp = false">
      <div class="menu-card help-card" @click.stop>
        <h3>{{ howToPlay }}</h3>
        <ul class="help-list" v-html="helpContent"></ul>
        <button @click="showHelp = false" class="start-btn sm">
          {{ closeHelp }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "QRLandGame",
  props: {
    value: {
      type: Object,
      default: () => ({
        type: "MeepMeepProRunner",
        texts: {
          title: "MeepMeep Run",
          subtitle: "بپر و امتیاز بگیر",
          scoreLabel: "امتیاز",
          levelLabel: "مرحله",
          comboLabel: "کومبو",
          startBtn: "شروع",
          helpBtn: "راهنما",
          gameOver: "پایان بازی",
          newRecordMessage: "رکورد جدید!",
          finalScore: "امتیاز نهایی",
          maxCombo: "بیشترین کومبو",
          restartBtn: "دوباره",
          rewardBtn: "جایزه",
          submitScore: "ثبت امتیاز",
          phonePrompt: "برای دریافت جایزه، شماره موبایل خود را وارد کنید:",
          phonePlaceholder: "۰۹۱۲XXXXXXX",
          submitting: "در حال ثبت...",
          submitAndRestart: "ثبت و شروع دوباره",
          cancel: "انصراف",
          howToPlay: "چطوری بازی کنیم؟",
          closeHelp: "اوکی، بریم!",
          helpContent: "👈 لمس <b>چپ</b> صفحه: لاین چپ<br/>👉 لمس <b>راست</b> صفحه: لاین راست<br/>👆 لمس <b>وسط</b> صفحه: پریدن<br/>🔥 کومبو بزن تا امتیازت چند برابر شه!",
        },
        images: {
          player: require("@/assets/images/game/meeq.webp"),
          hero: require("@/assets/images/game/meeq_bg.webp"),
          bgFar: require("@/assets/images/game/background_meg2.webp"),
          bgNear: require("@/assets/images/game/Desert.webp"),
          traps: [
            require("@/assets/images/game/trip(1).webp"),
            require("@/assets/images/game/trip(2).webp"),
            require("@/assets/images/game/trip(3).webp"),
            require("@/assets/images/game/trip(4).webp"),
            require("@/assets/images/game/trip(5).webp"),
            require("@/assets/images/game/trip(6).webp"),
            require("@/assets/images/game/trip(7).webp"),
            require("@/assets/images/game/trip(8).webp"),
            require("@/assets/images/game/trip(9).webp"),
            require("@/assets/images/game/trip(10).webp"),
            require("@/assets/images/game/trip(11).webp"),
            require("@/assets/images/game/trip(12).webp"),
            require("@/assets/images/game/trip(13).webp"),
            require("@/assets/images/game/trip(14).webp"),
            require("@/assets/images/game/trip(15).webp"),
            require("@/assets/images/game/trip(16).webp"),
            require("@/assets/images/game/trip(17).webp"),
            require("@/assets/images/game/trip(18).webp"),
          ],
          phoneFrameBackground: "",
        },
        gameplay: {
          lanesCount: 3,
          baseSpeed: 4,
          maxSpeed: 15,
          speedStep: 0.5,
          jumpForce: -15,
          gravity: 0.8,
          minScoreToSubmit: 100,
        },
        colors: {
          background: "#588ffb",
          ground: "#f8af5d",
          hud: "#000000",
          accent: "#ffd700",
          particleHit: "#ff0000",
        },
        login: {
          requirePhoneNumber: true,
          phoneValidationPattern: "^09\\d{9}$",
          enableEmail: false,
          enableSocialLogin: false,
        },
      }),
    },
  },
  data() {
    return {
      shakeTime: 0,
      state: "start", // start, playing, paused, exploding, getPhone, gameover
      showHelp: false,
      soundEnabled: true,
      level: 0,
      score: 0,
      combo: 1,
      maxCombo: 1,
      distance: 0,
      speed: this.value.gameplay.baseSpeed || 4,
      highScore: 0,
      isNewRecord: false,
      phoneNumber: "",
      submitting: false,
      ctx: null,
      canvas: null,
      groundY: 0,
      player: { lane: 1, y: 0, vy: 0, width: 60, height: 60, isJumping: false },
      traps: [],
      laneXs: [],
      imgAssets: {},
      trapImages: [],
      lastTrapTime: 0,
      audioCtx: null,
      particles: [],
      animationFrameId: null,
    };
  },
  computed: {
    isValidPhone() {
      const pattern = this.value.login.phoneValidationPattern || "^09[0-9]{9}$";
      return new RegExp(pattern).test(this.phoneNumber);
    },
    title() {
      return this.value.texts.title || "MeepMeep Run";
    },
    subtitle() {
      return this.value.texts.subtitle || "بپر و امتیاز بگیر";
    },
    scoreLabel() {
      return this.value.texts.scoreLabel || "امتیاز";
    },
    levelLabel() {
      return this.value.texts.levelLabel || "مرحله";
    },
    comboLabel() {
      return this.value.texts.comboLabel || "کومبو";
    },
    startBtn() {
      return this.value.texts.startBtn || "شروع";
    },
    helpBtn() {
      return this.value.texts.helpBtn || "راهنما";
    },
    gameOver() {
      return this.value.texts.gameOver || "پایان بازی";
    },
    newRecordMessage() {
      return this.value.texts.newRecordMessage || "رکورد جدید!";
    },
    finalScore() {
      return this.value.texts.finalScore || "امتیاز نهایی";
    },
    maxComboLabel() {
      return this.value.texts.maxCombo || "بیشترین کومبو";
    },
    restartBtn() {
      return this.value.texts.restartBtn || "دوباره";
    },
    rewardBtn() {
      return this.value.texts.rewardBtn || "جایزه";
    },
    submitScore() {
      return this.value.texts.submitScore || "ثبت امتیاز";
    },
    phonePrompt() {
      return this.value.texts.phonePrompt || "برای دریافت جایزه، شماره موبایل خود را وارد کنید:";
    },
    phonePlaceholder() {
      return this.value.texts.phonePlaceholder || "۰۹۱۲XXXXXXX";
    },
    submittingText() {
      return this.value.texts.submitting || "در حال ثبت...";
    },
    submitAndRestart() {
      return this.value.texts.submitAndRestart || "ثبت و شروع دوباره";
    },
    cancel() {
      return this.value.texts.cancel || "انصراف";
    },
    howToPlay() {
      return this.value.texts.howToPlay || "چطوری بازی کنیم؟";
    },
    closeHelp() {
      return this.value.texts.closeHelp || "اوکی، بریم!";
    },
    helpContent() {
      return this.value.texts.helpContent || "👈 لمس <b>چپ</b> صفحه: لاین چپ<br/>👉 لمس <b>راست</b> صفحه: لاین راست<br/>👆 لمس <b>وسط</b> صفحه: پریدن<br/>🔥 کومبو بزن تا امتیازت چند برابر شه!";
    },
    lanesCount() {
      return this.value.gameplay.lanesCount || 3;
    },
    baseSpeed() {
      return this.value.gameplay.baseSpeed || 4;
    },
    maxSpeed() {
      return this.value.gameplay.maxSpeed || 15;
    },
    speedStep() {
      return this.value.gameplay.speedStep || 0.5;
    },
    jumpForce() {
      return this.value.gameplay.jumpForce || -15;
    },
    gravity() {
      return this.value.gameplay.gravity || 0.8;
    },
    minScoreToSubmit() {
      return this.value.gameplay.minScoreToSubmit || 100;
    },
    backgroundColor() {
      return this.value.colors.background || "#588ffb";
    },
    groundColor() {
      return this.value.colors.ground || "#f8af5d";
    },
    hudColor() {
      return this.value.colors.hud || "#000000";
    },
    particleHitColor() {
      return this.value.colors.particleHit || "#ff0000";
    },
    requirePhoneNumber() {
      return this.value.login.requirePhoneNumber !== undefined ? this.value.login.requirePhoneNumber : true;
    },
  },
  mounted() {
    this.highScore = Number(localStorage.getItem("qrland_high_score")) || 0;
    this.canvas = this.$refs.gameCanvas;
    this.ctx = this.canvas.getContext("2d");
    this.loadImages().then(() => this.initCanvas());
    window.addEventListener("resize", this.initCanvas);
  },
  methods: {
    async loadImages() {
      const promises = [];
      
      // Load individual images except traps
      for (const [key, src] of Object.entries(this.value.images)) {
        if (key !== "traps") {
          const img = new Image();
          img.crossOrigin = "anonymous"; // Handle cross-origin images
          img.src = src;
          this.imgAssets[key] = img;
          promises.push(new Promise((resolve) => {
            img.onload = () => resolve(img);
            img.onerror = () => resolve(null); // Resolve even if image fails to load
          }));
        }
      }
      
      // Clear previous trap images
      this.trapImages = [];
      
      // Load trap images
      const trapSources = this.value.images.traps || [];
      for (const src of trapSources) {
        const img = new Image();
        img.crossOrigin = "anonymous"; // Handle cross-origin images
        img.src = src;
        this.trapImages.push(img);
        promises.push(new Promise((resolve) => {
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null); // Resolve even if image fails to load
        }));
      }
      
      await Promise.all(promises);
    },
    initCanvas() {
      if (!this.$refs.wrapper) return;
      this.canvas.width = this.$refs.wrapper.clientWidth;
      this.canvas.height = this.$refs.wrapper.clientHeight;
      this.groundY = this.canvas.height * 0.85;
      const lw = this.canvas.width / this.lanesCount;
      this.laneXs = [];
      for (let i = 0; i < this.lanesCount; i++) {
        this.laneXs.push(lw * (i + 0.5));
      }
      this.player.y = this.groundY - this.player.height;
      this.draw();
    },
    startGame() {
      if (!this.audioCtx)
        this.audioCtx = new (window.AudioContext ||
          window.webkitAudioContext)();
      this.state = "playing";
      this.score = 0;
      this.level = 0;
      this.combo = 1;
      this.distance = 0;
      this.traps = [];
      this.particles = [];
      this.speed = this.baseSpeed;
      this.lastTrapTime = Date.now();
      this.animate();
    },
    togglePause() {
      if (this.state === "playing") {
        this.state = "paused";
        cancelAnimationFrame(this.animationFrameId);
      } else if (this.state === "paused") {
        this.state = "playing";
        this.animate();
      }
    },
    toggleSound() {
      this.soundEnabled = !this.soundEnabled;
    },
    handleInput(e) {
      if (this.state !== "playing") return;
      const rect = this.canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const x = clientX - rect.left;

      if (x < this.canvas.width * 0.3) {
        if (this.player.lane > 0) this.player.lane--;
      } else if (x > this.canvas.width * 0.7) {
        if (this.player.lane < this.lanesCount - 1) this.player.lane++;
      } else {
        if (!this.player.isJumping) {
          this.player.vy = this.jumpForce;
          this.player.isJumping = true;
          this.playSound(400, 100);
        }
      }
    },
    update() {
      if (this.state !== "playing") return;
      this.distance += this.speed;
      this.level = Math.floor(this.distance / 2000);
      this.speed = Math.min(
        this.baseSpeed + this.level * this.speedStep,
        this.maxSpeed,
      );
      this.score += (this.speed / 10) * this.combo;

      this.player.y += this.player.vy;
      this.player.vy += this.gravity;
      if (this.player.y >= this.groundY - this.player.height) {
        this.player.y = this.groundY - this.player.height;
        this.player.isJumping = false;
        this.player.vy = 0;
      }
      if (!this.player.isJumping && this.state === "playing") {
        if (Math.random() > 0.8) {
          const pX = this.laneXs[this.player.lane];
          this.particles.push({
            x: pX - Math.random() * 20,
            y: this.groundY - 5,
            vx: -this.speed * 0.5,
            vy: -Math.random() * 1.5,
            radius: Math.random() * 6 + 2,
            life: 1,
            type: "dust",
          });
        }
      }

      // ۲. کم کردن زمان لرزش دوربین (اگر مقدار داشت)
      if (this.shakeTime > 0) this.shakeTime--;
      const now = Date.now();
      if (now - this.lastTrapTime > 2000 - this.speed * 80) {
        this.traps.push({
          x: this.canvas.width + 50,
          lane: Math.floor(Math.random() * this.lanesCount),
          type: Math.floor(Math.random() * this.trapImages.length),
          passed: false,
        });
        this.lastTrapTime = now;
      }

      const pX = this.laneXs[this.player.lane];
      for (let i = this.traps.length - 1; i >= 0; i--) {
        const t = this.traps[i];
        t.x -= this.speed;
        if (!t.passed && t.x < pX) {
          t.passed = true;
          this.combo++;
        }
        if (
          t.lane === this.player.lane &&
          Math.abs(t.x - pX) < 30 &&
          this.player.y > this.groundY - 80
        ) {
          this.state = "exploding";
          this.createExplosion(pX, this.player.y + 20);
          return;
        }
      }
      this.traps = this.traps.filter((t) => t.x > -50);
    },
    createExplosion(x, y) {
      this.shakeTime = 15;
      if (navigator.vibrate) navigator.vibrate(200);
      for (let i = 0; i < 20; i++) {
        this.particles.push({
          x,
          y,
          vx: (Math.random() - 0.5) * 15,
          vy: (Math.random() - 0.5) * 15,
          life: 1,
          type: "explosion",
        });
      }
    },
    draw() {
      const ctx = this.ctx;
      const canvas = this.canvas;

      ctx.save();

      // افکت لرزش دوربین موقع انفجار
      if (this.shakeTime > 0) {
        const shake = Math.random() * 10 - 5;
        ctx.translate(shake, shake);
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // رسم پس‌زمینه و زمین
      this.drawLayer(this.imgAssets.bgFar, 0.3, this.groundY - 350, 300);
      this.drawLayer(this.imgAssets.bgNear, 1, this.groundY - 100, 100);
      ctx.fillStyle = this.groundColor;
      ctx.fillRect(0, this.groundY, canvas.width, canvas.height);

      // رسم تله‌ها
      this.traps.forEach((t) => {
        const img = this.trapImages[t.type];
        if (img) ctx.drawImage(img, t.x - 25, this.groundY - 50, 50, 50);
      });

      const pX = (canvas.width / 3) * (this.player.lane + 0.5);
      const charWidth = this.player.width;
      const bob = this.player.isJumping ? 0 : Math.sin(Date.now() * 0.005) * 10;

      // رسم سایه
      const shadowY = this.groundY - 5;
      const dist = Math.abs(
        this.player.y + bob - (this.groundY - this.player.height),
      );
      const shadowScale = Math.max(0.2, 1 - dist / 150);

      ctx.beginPath();
      ctx.fillStyle = `rgba(0, 0, 0, ${0.25 * shadowScale})`;
      ctx.ellipse(
        pX,
        shadowY,
        charWidth * 0.4 * shadowScale,
        6 * shadowScale,
        0,
        0,
        Math.PI * 2,
      );
      ctx.fill();

      // رسم کاراکتر
      if (this.imgAssets.roadrunner) {
        ctx.drawImage(
          this.imgAssets.roadrunner,
          pX - charWidth / 2,
          this.player.y + bob,
          charWidth,
          this.player.height,
        );
      }

      // رسم ذرات (هم انفجار هم گرد و غبار)
      this.particles.forEach((p, i) => {
        if (p.type === "dust") {
          // استایل ابری برای گرد و غبار
          ctx.fillStyle = `rgba(255, 255, 255, ${p.life * 0.4})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.radius * (2 - p.life), 0, Math.PI * 2); // غبار با گذشت زمان بزرگتر میشه
        } else {
          // استایل آتشی برای انفجار - استفاده از رنگ از تنظیمات
          ctx.fillStyle = `rgba(${this.hexToRgb(this.particleHitColor)}, ${p.life})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
        }
        ctx.fill();

        p.x += p.vx;
        p.y += p.vy;
        p.life -= 0.025;
        if (p.life <= 0) this.particles.splice(i, 1);
      });

      ctx.restore();

      if (this.state === "exploding" && this.particles.length === 0)
        this.gameOver();
    },
    drawLayer(img, ratio, y, h) {
      if (!img) return;
      let x = -(this.distance * ratio) % this.canvas.width;
      this.ctx.drawImage(img, x, y, this.canvas.width, h);
      this.ctx.drawImage(img, x + this.canvas.width, y, this.canvas.width, h);
    },
    animate() {
      if (this.state !== "playing" && this.state !== "exploding") return;
      this.update();
      this.draw();
      this.animationFrameId = requestAnimationFrame(this.animate);
    },
    gameOver() {
      this.state = "gameover";
      const score = Math.floor(this.score);
      if (score > this.highScore) {
        this.highScore = score;
        this.isNewRecord = true;
        localStorage.setItem("qrland_high_score", score);
      }
    },
    submitPhoneAndRestart() {
      this.submitting = true;
      // Emit event to parent component with score and phone number
      this.$emit('submit-score', {
        score: Math.floor(this.score),
        phoneNumber: this.phoneNumber
      });
      // اینجا API خودت رو صدا بزن
      setTimeout(() => {
        this.submitting = false;
        this.phoneNumber = "";
        this.startGame();
      }, 1500);
    },
    playSound(freq, dur) {
      if (!this.soundEnabled || !this.audioCtx) return;
      const osc = this.audioCtx.createOscillator();
      const g = this.audioCtx.createGain();
      osc.connect(g);
      g.connect(this.audioCtx.destination);
      osc.frequency.value = freq;
      g.gain.exponentialRampToValueAtTime(
        0.0001,
        this.audioCtx.currentTime + dur / 1000,
      );
      osc.start();
      osc.stop(this.audioCtx.currentTime + dur / 1000);
    },
    hexToRgb(hex) {
      // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
      const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
      hex = hex.replace(shorthandRegex, (m, r, g, b) => {
        return r + r + g + g + b + b;
      });

      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? 
        `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` 
        : '255, 0, 0'; // Default to red if invalid hex
    },
  },
};
</script>

<style scoped>
.game-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 480px;
  background: #588ffb;
  overflow: hidden;
  touch-action: none;
  direction: rtl;
  font-family: Tahoma, sans-serif;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  max-height: 100%;
  box-sizing: border-box;
}
canvas {
  display: block;
  width: 100%;
  height: 100%;
  flex: 1;
  max-width: 100%;
  max-height: 100%;
}

/* HUD */
.hud-layer {
  position: absolute;
  top: 15px;
  left: 15px;
  right: 15px;
  display: flex;
  justify-content: space-between;
  pointer-events: none;
  margin-left: 40px;
}
.hud-controls {
  display: flex;
  gap: 8px;
  pointer-events: auto;
}
.control-btn {
  background: rgba(0, 0, 0, 0.6);
  border: 2px solid #fff;
  color: white;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
}
.control-btn1 {
  border: 2px solid #eb11b5;
  background: rgba(0, 0, 0, 0.6);
  color: #eb11b5;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
}
.control-btn2 {
  border: 2px solid #11eb65;
  background: rgba(0, 0, 0, 0.6);
  color: #11eb65;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
}
.control-btn3 {
  border: 2px solid #eb4f11;
  background: rgba(0, 0, 0, 0.6);
  color: #eb4f11;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
}

.box-scores {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.score-box,
.level-box,
.combo-badge {
  background: rgba(0, 0, 0, 0.75);
  padding: 5px 15px;
  border-radius: 12px;
  border: 2px solid #ffd700;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
}
.level-box {
  border-color: #00ff0d;
}

/* Overlays */
.overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  z-index: 10;
}
.z-top {
  z-index: 100;
}
.menu-card {
  background: #fff;
  padding: 25px;
  border-radius: 20px;
  text-align: center;
  width: 85%;
  max-width: 350px;
  border: 5px solid #f1c40f;
}
.hero-img {
  height: 140px;
  margin-bottom: 15px;
}
.start-btn {
  background: #f1c40f;
  color: #222;
  border: none;
  padding: 12px 25px;
  font-size: 1.1rem;
  font-weight: bold;
  border-radius: 50px;
  cursor: pointer;
  width: 100%;
}
.start-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.start-btn.sm {
  font-size: 0.9rem;
  padding: 8px 15px;
}
.help-link {
  background: none;
  border: none;
  color: #2980b9;
  margin-top: 12px;
  cursor: pointer;
  display: block;
  width: 100%;
}

/* Input */
.custom-input {
  width: 100%;
  padding: 12px;
  border: 2px solid #ddd;
  border-radius: 10px;
  font-size: 1.2rem;
  text-align: center;
  margin-top: 10px;
  outline: none;
}
.custom-input:focus {
  border-color: #f1c40f;
}

.stats-grid {
  display: flex;
  justify-content: space-around;
  margin: 20px 0;
  background: #f9f9f9;
  padding: 10px;
  border-radius: 10px;
}
.stat-item {
  display: flex;
  flex-direction: column;
}
.help-list {
  text-align: right;
  line-height: 1.8;
  margin: 15px 0;
}
.boom-text {
  color: #e74c3c;
  font-size: 2.5rem;
  margin-bottom: 10px;
}
.new-record {
  color: #27ae60;
  font-weight: bold;
  margin-top: 5px;
}
.pulse {
  animation: pulse-anim 2s infinite;
}
@keyframes pulse-anim {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}
.animate-pop {
  animation: pop 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
@keyframes pop {
  from {
    transform: scale(0.5);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.floating {
  animation: float 3s ease-in-out infinite;
}
@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}
.label {
  color: #001929;
  font-size: 11px;

  font-weight: bold;
  margin-bottom: -2px;
  border-radius: 16px;
  width: 100%;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  letter-spacing: 0.5px;
}
.value {
  color: #ffffff;
  font-size: 18px;
  font-weight: 900;
  text-shadow: 0 0 10px rgba(255, 215, 0, 0.3);
}
.combo-badge {
  border-color: #e74c3c;
  background: rgba(231, 76, 60, 0.2);
  animation: pulse 0.5s infinite alternate;
}
</style>
